import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useBulkAsignarTickets, useFetchAgentesMiDepartamento } from '@/shared/actions/erp/tickets-soporte-cliente';
import type { IUsuarioERP } from '@/shared/interfaces/erp/tickets-soporte-clientes';

type ModalBulkAsignarProps = {
  open: boolean;
  onClose: () => void;
  ticketIds: string[];
  onSuccess: (operacionId: string) => void;
};

export default function ModalBulkAsignar({ open, onClose, ticketIds, onSuccess }: ModalBulkAsignarProps) {
  const [selectedUser, setSelectedUser] = useState<IUsuarioERP | null>(null);
  const [userSearch, setUserSearch] = useState('');

  // Asignacion masiva: solo agentes del departamento del supervisor.
  // Backend filtra por profile.rol=AGENTE + mismo departamento via
  // X-User-Id. El modal se abre con la lista cargada (sin minimo 2 chars).
  const { data: usersRes, isLoading: isLoadingUsers } = useFetchAgentesMiDepartamento(
    userSearch,
    open,
  );
  const usuarios: IUsuarioERP[] = (usersRes as any)?.data?.data || (usersRes as any)?.data || [];

  const bulkAsignar = useBulkAsignarTickets();

  const handleSubmit = () => {
    if (!selectedUser) return;
    bulkAsignar.mutate(
      {
        ticket_ids: ticketIds,
        usuario_asignado_id: selectedUser.id,
        usuario_asignado_nombre: selectedUser.nombre_completo,
      },
      {
        onSuccess: (res) => {
          const operacionId = (res as any)?.data?.data?.operacion_id;
          handleClose();
          if (operacionId) onSuccess(operacionId);
        },
      }
    );
  };

  const handleClose = () => {
    setSelectedUser(null);
    setUserSearch('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Asignar Tickets Masivamente</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Se asignarán <strong>{ticketIds.length}</strong> ticket(s) al usuario seleccionado.
          </Alert>

          <Autocomplete
            options={usuarios}
            getOptionLabel={(o) => `${o.nombre_completo} (${o.username})`}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={selectedUser}
            onChange={(_, v) => setSelectedUser(v)}
            inputValue={userSearch}
            onInputChange={(_, v, reason) => {
              if (reason === 'input') setUserSearch(v);
              else if (reason === 'clear') setUserSearch('');
              else if (reason === 'reset') setUserSearch(v);
            }}
            loading={isLoadingUsers}
            noOptionsText={
              userSearch.length < 2 ? 'Escriba al menos 2 caracteres para buscar' : 'Sin resultados'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar Usuario"
                size="small"
                placeholder="Nombre, username o email..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingUsers ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Stack>
                  <Typography variant="body2" fontWeight={600}>
                    {option.nombre_completo}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.username} | {option.email}
                    {option.area ? ` | ${option.area}` : ''}
                  </Typography>
                </Stack>
              </Box>
            )}
          />

          {!isLoadingUsers && usuarios.length === 0 && (
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              No se encontró ningún usuario con ese criterio.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedUser || bulkAsignar.isPending}
        >
          {bulkAsignar.isPending ? 'Iniciando...' : `Asignar (${ticketIds.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
